import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience';
import { noop } from '@openx/utils/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { MatchedUsersOption, type MatchedUsersOptions } from '../constants';
import { getMatchedUsersLabelsMap } from '../utils/getMatchedUsersLabelsMap';
import { EidsDropdown } from './EidsDropdown';

type MatchedUsersDropdownProps = {
  accountUid: string;
  onChange: (value?: string | Set<string>, givenType?: AudienceOption) => void;
  params: AudienceData | null;
};

export const MatchedUsersDropdown = ({ accountUid, onChange, params }: MatchedUsersDropdownProps) => {
  const { t } = useTranslation();

  const inputState = params?.[AudienceOption.MATCHED_USERS] || null;

  const selectedEids = params?.[AudienceOption.TRAFFIC_FILTER_EIDS];
  const hasSelectedEids = !!selectedEids && selectedEids.size > 0;

  const { useAccountFetch = noop } = useTargetingContext();
  const { account, isAccountLoading } = useAccountFetch(accountUid) || { account: null, isAccountLoading: false };
  const eidsAllowedByAccount = account?.exchange?.traffic_filter_eids;

  const [showEidsDropdown, setShowEidsDropdown] = useState(false);
  const optionsLables = getMatchedUsersLabelsMap(t);

  const options = useMemo(() => {
    const availableOptions: MatchedUsersOptions[] = [
      MatchedUsersOption.Matched,
      MatchedUsersOption.Unmatched,
      MatchedUsersOption.MatchedByCookie,
    ];

    const eidsAllowed = !!eidsAllowedByAccount && Object.keys(eidsAllowedByAccount).length > 0;
    if (eidsAllowed || hasSelectedEids || inputState === MatchedUsersOption.MatchedByEids) {
      availableOptions.push(MatchedUsersOption.MatchedByEids);
    }

    return availableOptions;
  }, [eidsAllowedByAccount, hasSelectedEids, inputState]);

  const handleChange = useCallback(
    (val: string) => {
      onChange(val);
      onChange(undefined, AudienceOption.TRAFFIC_FILTER_EIDS);
    },
    [onChange]
  );

  useEffect(() => {
    const shouldShowEidsDropdown = inputState === MatchedUsersOption.MatchedByEids || hasSelectedEids;
    setShowEidsDropdown(shouldShowEidsDropdown);
  }, [hasSelectedEids, inputState]);

  return (
    <>
      <AutoComplete
        data-test="matched-users-options-input"
        getOptionLabel={option => optionsLables[option].name}
        onChange={(_, val) => val && handleChange(val)}
        options={options}
        textFieldProps={{ placeholder: t('[ Select Matched Users ]') }}
        value={inputState}
        loading={isAccountLoading}
      />

      {showEidsDropdown && (
        <EidsDropdown eidsAllowedByAccount={eidsAllowedByAccount} selectedEids={selectedEids} onChange={onChange} />
      )}
    </>
  );
};
